import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

/// Общая рамка поля: подпись сверху, ошибка/подсказка снизу.
///
/// Material рисовал подпись «плавающей» внутри рамки; здесь она статична —
/// в плотных админских формах так меньше прыжков при фокусе.
function FieldShell({
  id,
  label,
  error,
  hint,
  className,
  children,
}: {
  id: string
  label?: ReactNode
  error?: string | null
  hint?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={id} className="text-[13px] font-semibold text-ink-strong">
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-ink-muted">{hint}</p>
      ) : null}
    </div>
  )
}

/// Поле — утопленная плашка, а не прямоугольник в рамке: форма из шести полей
/// в обводках выглядела решёткой. Границу заменяет разница подложек, а фокус
/// показывает кольцо (`ring` — это тень, размеров она не меняет и текст не
/// дёргает).
const CONTROL = [
  'w-full rounded-field bg-surface-alt text-sm text-ink',
  'transition duration-150 ease-out-soft',
  'placeholder:text-ink-faint',
  'hover:bg-surface-sunken',
  'focus:bg-surface focus:ring-2 focus:ring-brand focus:outline-none hover:focus:bg-surface',
  'disabled:cursor-not-allowed disabled:bg-surface-alt/60 disabled:text-ink-disabled disabled:hover:bg-surface-alt/60',
].join(' ')

/// Ошибка красит саму подложку: у поля без рамки краснеть больше нечему.
const CONTROL_ERROR = 'bg-danger/8 ring-2 ring-danger/45 hover:bg-danger/8'

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode
  error?: string | null
  hint?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  containerClassName?: string
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, leading, trailing, className, containerClassName, id, ...rest },
  ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId

  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} className={containerClassName}>
      <div className="relative flex items-center">
        {leading ? (
          <span className="pointer-events-none absolute left-3 flex text-ink-faint">{leading}</span>
        ) : null}
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          className={cn(
            CONTROL,
            'h-11 px-4',
            leading && 'pl-10',
            trailing && 'pr-10',
            error && CONTROL_ERROR,
            className,
          )}
          {...rest}
        />
        {trailing ? <span className="absolute right-1.5 flex items-center">{trailing}</span> : null}
      </div>
    </FieldShell>
  )
})

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode
  error?: string | null
  hint?: ReactNode
  containerClassName?: string
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, className, containerClassName, id, rows = 4, ...rest },
  ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId

  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} className={containerClassName}>
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        className={cn(
          CONTROL,
          'resize-y px-4 py-3 leading-relaxed',
          error && CONTROL_ERROR,
          className,
        )}
        {...rest}
      />
    </FieldShell>
  )
})

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode
  error?: string | null
  hint?: ReactNode
  containerClassName?: string
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, className, containerClassName, id, children, ...rest },
  ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId

  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} className={containerClassName}>
      <select
        ref={ref}
        id={fieldId}
        className={cn(
          CONTROL,
          'h-11 cursor-pointer appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat px-4 pr-10',
          error && CONTROL_ERROR,
          className,
        )}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B665C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        }}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  )
})
